import { useEffect, useRef, type RefObject } from 'react';
import maplibregl from 'maplibre-gl';
import { captureException } from '@/lib/sentry';

/**
 * useFarmChips — HTML-чипы company / cluster / farm над картой.
 *
 * Mirrors frontend/src/lib/map/useFarmChips.ts (anti-collision dedupe +
 * level-based visibility). Адаптировано под frontend-vite — без зависимости
 * от next/router.
 */

export interface FarmMarker {
  id: string;
  name: string;
  cluster_id: string | null;
  cluster_name: string | null;
  lng: number;
  lat: number;
  field_count: number;
  area_ha: number;
  bbox?: [number, number, number, number] | null;
}

export interface ClusterMarker {
  id: string;
  name: string;
  lng: number;
  lat: number;
  field_count: number;
  farm_count: number;
  area_ha: number;
  bbox?: [number, number, number, number] | null;
}

export interface FarmSubMarker {
  farm_id: string;
  sub_id: number;
  name: string;
  cluster_id: string | null;
  cluster_name: string | null;
  lng: number;
  lat: number;
  field_count: number;
  area_ha: number;
  bbox: [number, number, number, number];
}

interface FarmChipsCompany {
  id: string;
  name: string;
}

interface Args {
  mapRef: RefObject<maplibregl.Map | null>;
  markers: FarmMarker[];
  clusterMarkers: ClusterMarker[];
  subMarkers: FarmSubMarker[];
  farmFilter: string | null | undefined;
  clusterFilter: string | null | undefined;
  userLevel: number;
  companies: FarmChipsCompany[];
  companyRevealed: boolean;
  setCompanyRevealed: (v: boolean) => void;
  persistScope: (type: 'farm' | 'cluster', id: string) => void;
  onSubMarkerClick?: (s: FarmSubMarker) => void;
}

type ChipVariant = 'company' | 'cluster' | 'farm';
interface ChipItem {
  id: string;
  name: string;
  lng: number;
  lat: number;
  area_ha: number;
  onClick: () => void;
  variant: ChipVariant;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[c] as string,
  );
}

export function useFarmChips({
  mapRef,
  markers,
  clusterMarkers,
  subMarkers,
  farmFilter,
  clusterFilter,
  userLevel,
  companies,
  companyRevealed,
  setCompanyRevealed,
  persistScope,
  onSubMarkerClick,
}: Args): void {
  const markersRef = useRef<maplibregl.Marker[]>([]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const dedupOverlap = () => {
      const items = markersRef.current.map((m) => {
        m.setOffset([0, 0]);
        return m;
      });
      requestAnimationFrame(() => {
        interface Box {
          m: maplibregl.Marker;
          left: number;
          right: number;
          top: number;
          bottom: number;
        }
        // AK-297 (design review 2026-06-27): на плотных группах
        // (Цимлянский/Аргамак/Будённовский) чипы налезали друг на друга —
        // прежний дедуп двигал только ВНИЗ шагом 4px без зазора, так что
        // соседние pill'ы вставали впритык и читались как один блок.
        //
        // Усиление: (1) GAP между чипами — раздвигаем не «до касания», а с
        // воздухом; (2) симметричное раздвигание — чип уезжает в ту сторону
        // (вверх/вниз) от центра группы, куда ближе, поэтому плотный кластер
        // расходится веером в обе стороны, а не тянется длинной колонкой вниз;
        // (3) больший шаг поиска свободного места.
        const GAP = 6; // px воздуха между соседними pill'ами
        const STEP = 6; // шаг поиска свободного слота
        const boxes: Box[] = items
          .map((m) => {
            const r = m.getElement().getBoundingClientRect();
            return { m, left: r.left, right: r.right, top: r.top, bottom: r.bottom };
          })
          .sort((a, b) => a.top - b.top);
        // Центр группы по вертикали — относительно него выбираем направление
        // раздвигания (чтобы кластер расходился в обе стороны, а не вниз).
        const groupMidY =
          boxes.length > 0
            ? boxes.reduce((s, b) => s + (b.top + b.bottom) / 2, 0) / boxes.length
            : 0;
        const placed: Box[] = [];
        const overlaps = (a: Box, b: Box) =>
          !(
            a.right + GAP < b.left ||
            a.left - GAP > b.right ||
            a.bottom + GAP < b.top ||
            a.top - GAP > b.bottom
          );
        for (const b of boxes) {
          const h = b.bottom - b.top;
          const dir = (b.top + b.bottom) / 2 >= groupMidY ? 1 : -1;
          let dy = 0;
          const candidate = { ...b };
          let safety = 0;
          while (placed.some((p) => overlaps(candidate, p)) && safety < 200) {
            dy += STEP * dir;
            candidate.top = b.top + dy;
            candidate.bottom = b.top + dy + h;
            safety++;
          }
          if (dy !== 0) b.m.setOffset([0, dy]);
          placed.push(candidate);
        }
      });
    };

    const apply = () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];

      const chips: ChipItem[] = [];
      const hasFilter = !!(farmFilter || clusterFilter);
      const showCompany = !hasFilter && userLevel === 12 && !companyRevealed;
      const showClusters =
        !hasFilter &&
        ((userLevel >= 9 && userLevel <= 11) ||
          (userLevel === 12 && companyRevealed));
      const showFarms = !hasFilter && userLevel >= 7 && userLevel <= 9;

      if (showCompany && clusterMarkers.length > 0) {
        const cx =
          clusterMarkers.reduce((s, c) => s + c.lng, 0) / clusterMarkers.length;
        const cy =
          clusterMarkers.reduce((s, c) => s + c.lat, 0) / clusterMarkers.length;
        chips.push({
          id: 'company',
          name: companies[0]?.name ?? 'Компания',
          lng: cx,
          lat: cy,
          area_ha: clusterMarkers.reduce((s, c) => s + c.area_ha, 0),
          onClick: () => setCompanyRevealed(true),
          variant: 'company',
        });
      }

      if (showClusters) {
        const list = clusterFilter
          ? clusterMarkers.filter((c) => c.id === clusterFilter)
          : clusterMarkers;
        for (const c of list) {
          chips.push({
            id: c.id,
            name: c.name,
            lng: c.lng,
            lat: c.lat,
            area_ha: c.area_ha,
            onClick: () => persistScope('cluster', c.id),
            variant: 'cluster',
          });
        }
      }

      const addFarmChip = (m: FarmMarker) => {
        chips.push({
          id: m.id,
          name: m.name,
          lng: m.lng,
          lat: m.lat,
          area_ha: m.area_ha,
          onClick: () => persistScope('farm', m.id),
          variant: 'farm',
        });
      };

      if (farmFilter) {
        if (userLevel >= 8) {
          const subs = subMarkers.filter((s) => s.farm_id === farmFilter);
          if (subs.length > 1) {
            for (const s of subs) {
              chips.push({
                id: `${s.farm_id}-sub-${s.sub_id}`,
                name: `${s.field_count} полей${
                  s.sub_id >= 0 ? ' · массив ' + (s.sub_id + 1) : ''
                }`,
                lng: s.lng,
                lat: s.lat,
                area_ha: s.area_ha,
                onClick: () => onSubMarkerClick?.(s),
                variant: 'farm',
              });
            }
          } else {
            const selectedFarm = markers.find((m) => m.id === farmFilter);
            if (selectedFarm) addFarmChip(selectedFarm);
          }
        }
      } else if (clusterFilter) {
        for (const m of markers.filter((x) => x.cluster_id === clusterFilter)) {
          addFarmChip(m);
        }
      } else if (showFarms) {
        for (const m of markers) addFarmChip(m);
      }

      for (const c of chips) {
        if (!Number.isFinite(c.lng) || !Number.isFinite(c.lat)) continue;
        try {
          const el = document.createElement('button');
          el.className = `mapx-chip mapx-chip-${c.variant}`;
          const area = Math.round(c.area_ha).toLocaleString('ru-RU');
          el.innerHTML = `
            <span class="mapx-chip-name">${escapeHtml(c.name)}</span>
            <span class="mapx-chip-area">${area} га</span>
          `;
          el.addEventListener('click', (ev) => {
            ev.stopPropagation();
            c.onClick();
          });
          const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
            .setLngLat([c.lng, c.lat])
            .addTo(map);
          markersRef.current.push(marker);
        } catch (e) {
          if (import.meta.env.DEV) console.error('[map] failed to add chip:', c, e);
          else captureException(e, { tags: { source: 'useFarmChips.addChip' } });
        }
      }

      dedupOverlap();
    };

    map.on('moveend', dedupOverlap);
    apply();

    return () => {
      map.off('moveend', dedupOverlap);
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    markers,
    clusterMarkers,
    subMarkers,
    farmFilter,
    clusterFilter,
    userLevel,
    companies,
    companyRevealed,
  ]);
}
